import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { useAuthStore } from '../store/authStore';
import { db } from '../services/firebase';
import './AIAgents.css';

const AIAgents = () => {
  const { user } = useAuthStore();
  const [agentLogs, setAgentLogs] = useState([]);
  const [agentFlags, setAgentFlags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const functions = getFunctions();

  // Agent configurations
  const agents = [
    {
      id: 'monthlyBilling',
      name: 'Monthly Billing',
      description: 'Automatically generates and sends invoices on the 1st of each month',
      schedule: '1st of month, 9:00 AM',
      status: 'active',
      icon: '📅',
    },
    {
      id: 'paymentFollowUp',
      name: 'Payment Follow-Up',
      description: 'Intelligent payment reminders with personalized messaging',
      schedule: 'Daily, 10:00 AM',
      status: 'active',
      icon: '💰',
    },
    {
      id: 'clientHealth',
      name: 'Client Health Monitor',
      description: 'Predicts churn risk and identifies relationship issues',
      schedule: 'Weekly, Monday 8:00 AM',
      status: 'active',
      icon: '❤️',
    },
    {
      id: 'cashFlowForecast',
      name: 'Cash Flow Forecast',
      description: 'Predicts cash flow and identifies financial gaps',
      schedule: 'Weekly, Monday 9:00 AM',
      status: 'active',
      icon: '📊',
    },
    {
      id: 'timeAnomaly',
      name: 'Time Tracking Auditor',
      description: 'Detects missing billable hours and time entry issues',
      schedule: 'Daily, 6:00 PM',
      status: 'active',
      icon: '⏱️',
    },
  ];

  useEffect(() => {
    if (!user) return;

    // Subscribe to agent logs
    const logsQuery = query(
      collection(db, 'agentLogs'),
      orderBy('timestamp', 'desc'),
      limit(50)
    );

    const unsubscribeLogs = onSnapshot(logsQuery, (snapshot) => {
      const logs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAgentLogs(logs);
    });

    // Subscribe to agent flags
    const flagsQuery = query(
      collection(db, 'agentFlags'),
      orderBy('createdAt', 'desc'),
      limit(20)
    );

    const unsubscribeFlags = onSnapshot(flagsQuery, (snapshot) => {
      const flags = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAgentFlags(flags);
    });

    return () => {
      unsubscribeLogs();
      unsubscribeFlags();
    };
  }, [user, db]);

  const triggerAgent = async (agentName) => {
    setLoading(true);
    try {
      const trigger = httpsCallable(functions, 'triggerAgent');
      const result = await trigger({ agentName });
      alert(`Agent ${agentName} triggered successfully!`);
    } catch (error) {
      console.error('Error triggering agent:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const getSeverityColor = (severity) => {
    const colors = {
      low: '#4caf50',
      medium: '#ff9800',
      high: '#f44336',
      critical: '#d32f2f',
    };
    return colors[severity] || '#999';
  };

  return (
    <div className="ai-agents-page">
      <div className="page-header">
        <h1>🤖 AI Agent Control Center</h1>
        <p>Autonomous billing operations powered by Claude AI</p>
      </div>

      <div className="tabs">
        <button
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={activeTab === 'logs' ? 'active' : ''}
          onClick={() => setActiveTab('logs')}
        >
          Activity Logs
        </button>
        <button
          className={activeTab === 'flags' ? 'active' : ''}
          onClick={() => setActiveTab('flags')}
        >
          Flagged Issues ({agentFlags.filter((f) => f.status === 'pending').length})
        </button>
      </div>

      {activeTab === 'overview' && (
        <div className="agents-grid">
          {agents.map((agent) => (
            <div key={agent.id} className="agent-card">
              <div className="agent-icon">{agent.icon}</div>
              <h3>{agent.name}</h3>
              <p className="agent-description">{agent.description}</p>
              <div className="agent-meta">
                <span className="schedule">📆 {agent.schedule}</span>
                <span className={`status ${agent.status}`}>
                  {agent.status === 'active' ? '✓ Active' : '⏸ Paused'}
                </span>
              </div>
              <div className="agent-stats">
                {agentLogs
                  .filter((log) => log.agentName === agent.id || log.agentName === agent.name.replace(/\s/g, ''))
                  .slice(0, 1)
                  .map((log) => (
                    <div key={log.id} className="last-run">
                      <small>Last run: {formatTimestamp(log.timestamp)}</small>
                      <small className={log.status === 'completed' ? 'success' : 'error'}>
                        {log.status}
                      </small>
                    </div>
                  ))}
              </div>
              <button
                className="trigger-btn"
                onClick={() => triggerAgent(agent.id)}
                disabled={loading}
              >
                Run Now
              </button>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'logs' && (
        <div className="activity-logs">
          <h2>Recent Activity</h2>
          <div className="logs-table">
            <table>
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Agent</th>
                  <th>Status</th>
                  <th>Duration</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {agentLogs.map((log) => (
                  <tr key={log.id}>
                    <td>{formatTimestamp(log.timestamp)}</td>
                    <td>{log.agentName}</td>
                    <td>
                      <span className={`badge ${log.status}`}>{log.status}</span>
                    </td>
                    <td>{log.duration ? `${(log.duration / 1000).toFixed(1)}s` : 'N/A'}</td>
                    <td>
                      {log.result?.invoicesGenerated && (
                        <span>📄 {log.result.invoicesGenerated} invoices</span>
                      )}
                      {log.result?.remindersSent && (
                        <span>📧 {log.result.remindersSent} reminders</span>
                      )}
                      {log.result?.message && (
                        <span className="log-message">{log.result.message.substring(0, 50)}...</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'flags' && (
        <div className="flagged-issues">
          <h2>Issues Requiring Attention</h2>
          <div className="flags-list">
            {agentFlags.map((flag) => (
              <div
                key={flag.id}
                className="flag-card"
                style={{ borderLeftColor: getSeverityColor(flag.severity) }}
              >
                <div className="flag-header">
                  <span className="flag-agent">{flag.agentName}</span>
                  <span
                    className="flag-severity"
                    style={{ backgroundColor: getSeverityColor(flag.severity) }}
                  >
                    {flag.severity}
                  </span>
                </div>
                <h4>{flag.issueType}</h4>
                <p>{flag.description}</p>
                {flag.data && (
                  <pre className="flag-data">{JSON.stringify(flag.data, null, 2)}</pre>
                )}
                <div className="flag-footer">
                  <small>{formatTimestamp(flag.createdAt)}</small>
                  {flag.status === 'pending' && (
                    <button className="resolve-btn">Mark Resolved</button>
                  )}
                </div>
              </div>
            ))}
            {agentFlags.length === 0 && (
              <div className="no-flags">
                <p>✨ No issues flagged. All systems running smoothly!</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAgents;
